import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { z } from 'zod'
import { useEffect, useCallback } from 'react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { TelegramAuth } from '@/widgets/telegram-auth'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { Gift, ChevronRight } from 'lucide-react'
import { ProfileHeader } from '@/widgets/profile-header'
import { ProfileActionsGrid } from '@/widgets/profile-actions-grid'
import { ProfileTeaStats } from '@/widgets/profile-tea-stats'
import { ProfileDiaryPreview } from '@/widgets/profile-diary-preview'
import { ProfileSettings } from '@/widgets/profile-settings'

const profileSearchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/profile')({
  validateSearch: profileSearchSchema,
  component: ProfilePage,
})

/**
 * Страница профиля
 * 
 * Премиальный личный кабинет — чистый, быстрый, удобный.
 * Один вертикальный поток с ключевыми блоками.
 */
function ProfilePage() {
  const { redirect } = Route.useSearch()
  const { initData, hapticFeedback } = useTelegram()
  const navigate = useNavigate()
  
  // BackButton → назад по истории
  const handleBack = useCallback(() => {
    hapticFeedback.impactOccurred('light')
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: '/' })
    }
  }, [hapticFeedback, navigate])
  
  useBackButton(true, handleBack)

  // Редирект после авторизации
  useEffect(() => {
    if (redirect && initData) {
      const timer = setTimeout(() => {
        navigate({ to: redirect as any })
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [redirect, initData, navigate])

  const isAuthenticated = !!initData

  return (
    <AppShell showBottomNav>
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0D0F11] overscroll-contain">
        <div className="px-4 py-6 pb-safe max-w-lg mx-auto">
          {!isAuthenticated ? (
            <TelegramAuth />
          ) : (
            <div className="space-y-4">
              {/* Хедер профиля */}
              <ProfileHeader />

              {/* Быстрые действия */}
              <ProfileActionsGrid />

              {/* Активность за неделю */}
              <ProfileTeaStats />

              {/* Дневник */}
              <ProfileDiaryPreview />

              {/* Подарочные сертификаты */}
              <Link to="/my-gift-cards" className="block">
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-[#1C1E21] border border-gray-100 dark:border-gray-800 active:scale-[0.98] transition-transform">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Gift className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Мои сертификаты</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">История покупок и статус</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                </div>
              </Link>

              {/* Настройки */}
              <ProfileSettings />

              {/* Версия */}
              <p className="text-center text-xs text-gray-400 dark:text-gray-600 pt-2">
                Версия 1.0.0
              </p>
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </AppShell>
  )
}
